import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, MapPin, Phone, ArrowRight, X } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription, SheetClose } from "@/components/ui/sheet";
import { BUSINESS, NAV_LINKS } from "@/config/site";
import { WhatsAppButton } from "@/components/site/WhatsAppButton";
import { trackEvent } from "@/lib/analytics";

export const Wordmark = ({ light = false }) => (
  <span className="flex flex-col leading-none">
    <span className={`font-display text-[22px] font-semibold tracking-tight sm:text-2xl ${light ? "text-[#FAF9F5]" : "text-[color:var(--u-green)]"}`}>Uhlumo</span>
    <span className={`mt-0.5 text-[10px] font-semibold uppercase tracking-[0.32em] ${light ? "text-[#cfe2c9]" : "text-[color:var(--u-brown)]"}`}>Organics</span>
  </span>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <div className="surface-green text-[13px]">
        <div className="container-site flex h-9 items-center justify-between gap-4">
          <span className="flex items-center gap-1.5 truncate text-[#dfe9dc]">
            <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            Gugulethu, Cape Town · Natural wellness products
          </span>
          <a href={`tel:${BUSINESS.phoneE164}`} className="hidden items-center gap-1.5 font-semibold text-[#FAF9F5] hover:underline sm:flex" onClick={() => trackEvent("phone_click", { location: "topbar" })} data-testid="topbar-phone-link">
            <Phone className="h-3.5 w-3.5" aria-hidden="true" />
            {BUSINESS.phoneDisplay}
          </a>
        </div>
      </div>
      <header className={`sticky top-0 z-40 border-b transition-shadow ${scrolled ? "shadow-[0_6px_24px_rgba(37,51,43,0.06)]" : ""}`} style={{ background: "rgba(250,249,245,0.94)", backdropFilter: "blur(10px)", borderColor: "rgba(40,91,69,0.12)" }}>
        <div className="container-site flex h-[72px] items-center justify-between gap-6">
          <Link to="/" aria-label="Uhlumo Organics — home" data-testid="site-header-wordmark">
            <Wordmark />
          </Link>
          <nav aria-label="Main" className="hidden lg:block">
            <ul className="flex items-center gap-1">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    data-testid={l.testid}
                    className={({ isActive }) =>
                      `rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors ${isActive ? "text-[color:var(--u-green)] bg-[#EEF3EC]" : "text-[#3d4a42] hover:text-[color:var(--u-green)] hover:bg-[#F1F4EE]"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/products" className="btn btn-primary hidden sm:inline-flex" data-testid="site-header-explore-products-button">
              Explore Products
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <button
              type="button"
              className="btn btn-outline h-11 w-11 px-0 lg:hidden"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              aria-expanded={open}
              aria-controls="mobile-menu"
              data-testid="site-header-mobile-menu-button"
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" id="mobile-menu" className="w-[88vw] max-w-sm bg-[#FAF9F5] p-0 [&>button]:hidden" data-testid="mobile-menu">
          <div className="flex items-center justify-between border-b px-6 py-5">
            <SheetTitle asChild>
              <span>
                <Wordmark />
              </span>
            </SheetTitle>
            <SheetClose className="btn btn-outline h-10 w-10 px-0" aria-label="Close menu" data-testid="mobile-menu-close-button">
              <X className="h-5 w-5" aria-hidden="true" />
            </SheetClose>
          </div>
          <SheetDescription className="sr-only">Site navigation</SheetDescription>
          <nav aria-label="Mobile" className="px-4 py-4">
            <ul className="space-y-1">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    data-testid={`mobile-${l.testid}`}
                    className={({ isActive }) => `flex min-h-[48px] items-center rounded-xl px-4 font-display text-xl font-semibold ${isActive ? "bg-[#EEF3EC] text-[color:var(--u-green)]" : "text-[#25332B] hover:bg-[#F1F4EE]"}`}
                  >
                    {l.label}
                  </NavLink>
                </li>
              ))}
              <li>
                <NavLink to="/faqs" className="flex min-h-[48px] items-center rounded-xl px-4 font-display text-xl font-semibold text-[#25332B] hover:bg-[#F1F4EE]" data-testid="mobile-site-nav-faqs-link">
                  FAQs
                </NavLink>
              </li>
            </ul>
          </nav>
          <div className="space-y-3 border-t px-6 py-6">
            <Link to="/products" className="btn btn-primary w-full" data-testid="mobile-menu-explore-products">
              Explore Products
            </Link>
            <WhatsAppButton className="btn btn-whatsapp w-full" testid="mobile-menu-whatsapp" location="mobile-menu" />
            <p className="pt-2 text-sm text-muted-foreground">
              {BUSINESS.phoneDisplay} · {BUSINESS.email}
            </p>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
